import Vue from 'vue';
import api from "@modules/AudienceManager/api";

export default {
    namespaced: true,
    state: {
        /**
         * @type {Object.<String,Object>} Object containing keyValuePairs of audienceId/audience
         */
        audiences: {},
        /**
         * @type {Object.<String,Object>} Object containing keyValuePairs of channelId/channel
         */
        channels: {},
        loadingAudiences: false
    },
    getters: {
        /**
         * Return a single audience from state.
         * @param {Object} state Current vuex state.
         * @returns {Object} audience.
         */
        getAudience(state) {
            return audienceId => {
                return state.audiences[audienceId];
            };
        },
        /**
         * Return current state available channels.
         * @param {Object} state Current vuex state.
         */
        getAvailableChannels(state) {
            return state.availableChannels;    
        }
    },
    mutations: {
        /**
         * Set the current audiences dictionary.
         * @param {Object} state current vuex state.
         * @param {Array} audiences array with audiences.
         */
        setAudiences(state, audiences) {
            audiences.forEach((audience) => {
                audience.channels = audience.channels || [];
                Vue.set(state.audiences, audience.id, audience);
            });
        },
        /**
         * Remove an audience from state.
         * @param {Object} state current vuex state.
         * @param {String} id id of the audience to remove.
         */
        removeAudience(state, id) {
            Vue.delete(state.audiences, id);
        },
        /**
         * Set the channels of a single target audience.
         * @param {Object} state current vuex state.
         * @param {Object} payload payload with id and channels of the target audience.
         */
        setAudienceChannels(state, {id, channels}) {
            state.audiences[id].channels = channels;
        },
        /**
         * Set the current channels dictionary.
         * @param {Object} state Current vuex state.
         * @param {Array<Object>} channels Array with channels.
         */
        setChannels(state, channels) {
            channels.forEach((channel) => {
                Vue.set(state.channels, channel.id, channel);
            });
        },
        /**
         * Set the current channels dictionary.
         * @param {Object} state Current vuex state.
         * @param {Number} status Status to set the audience to.
         */
        setStatus(state, {id, status}) {
            state.audiences[id].status = status;
        },
        setLoading(state, loading) {
            state.loadingAudiences = loading;
        }
    },
    actions: {
        /**
         * Get audiences from api.
         * @param {Object} context vuex context
         */
        async getAudiences(context) {
            context.commit('setLoading', true);
            const audiences = await api.listAudiences();
            context.commit('setAudiences', audiences);
            context.commit('setLoading', false);
        },
        /**
         * Sets available channels.
         * @param {Object} Context Vuex context 
         */
        async getChannels({commit, state}) {
            
            if(state.channels.length > 0) {
                return;
            }
            
            const channels = await api.listChannels();
            commit('setChannels', channels);
        },
        /**
         * Delete a single audience.
         * @param {Object} context  vuex context
         * @param {String} id id of the audience to delete
         */
        async deleteAudience(context, id) {
            const result = await api.deleteAudience(id);

            if(!result.success) {
                throw `Failed to delete audience with id: ${id}`;
            } else {
                context.commit('removeAudience', id);
            }
        },
        /**
         * Post audience channels for single audience to api. 
         * @param {Object} context Vuex context
         * @param {Object} payload Object containing id and channels for target audience.
         */
        async postAudienceChannels(context, { id, channels }) {
            const result = await api.setChannels(id, channels);

            if(!result.success) {
                throw `Failed to set channels for audience with id: ${id}`;
            } else {
                context.commit('setAudienceChannels', { id, channels });
            }
        },
        /**
         * Post new status for single audience to api.
         * @param {Object} context Vuex context
         * @param {Object} payload Object containing id and status for target audience.
         */
        async postAudienceStatus(context, { id, status }) {
            const result = await api.setStatus(id, status);

            if(!result.success) {
                throw `Failed to set status for audience with id: ${id}`;
            } else {
                context.commit('setStatus', { id, status });
            }
        }
    }
};